<template>
  <div class="cap-page" @mousemove="handleParallax">
    <div class="bg-noise"></div>
    <div class="ambient-glow" :style="parallaxStyle"></div>

    <div class="container-wrapper">
      <section class="hero-section v-reveal">
        <div class="tagline">POWER OF COMMUNITY</div>
        <h1 class="main-title">核心能力<span class="dot">.</span></h1>
        <p class="hero-desc">
          来秋社区以技术为底座，以体验为核心，打造好用、稳定、有温度的服务体系。<br />
          从内容创作到AI互动，从社交连接到数字生活，我们用心做好每一处能力细节。
        </p>
        <div class="scroll-indicator">
          <div class="mouse"></div>
        </div>
      </section>

      <section class="pillars-section">
        <div class="section-header v-reveal">
          <h2 class="section-title">01 / 能力支柱</h2>
          <p class="section-subtitle">四大核心能力，支撑完整社区生态</p>
        </div>
        <div class="pillar-grid">
          <div v-for="(pillar, index) in corePillars" :key="index" class="pillar-card v-reveal">
            <div class="card-bg-mask"></div>
            <div class="pillar-icon" v-html="pillar.icon"></div>
            <h3>{{ pillar.title }}</h3>
            <p>{{ pillar.desc }}</p>
            <ul class="pillar-tags">
              <li v-for="tag in pillar.tags" :key="tag">{{ tag }}</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="tech-infra v-reveal">
        <div class="infra-content">
          <div class="text-side">
            <h2 class="section-title">02 / 技术底座</h2>
            <p class="infra-desc">采用现代化前端架构，搭配稳定服务支撑，保证全站流畅、快速、安全运行，为用户提供始终可靠的使用体验。</p>
            <div class="infra-metrics">
              <div class="metric-item">
                <span class="m-val">极速</span>
                <span class="m-lab">页面加载体验</span>
              </div>
              <div class="metric-item">
                <span class="m-val">稳定</span>
                <span class="m-lab">平台长期可用</span>
              </div>
            </div>
          </div>
          <div class="visual-side">
            <div class="tech-orb">
              <div class="ring r-1"></div>
              <div class="ring r-2"></div>
              <div class="ring r-3"></div>
            </div>
          </div>
        </div>
      </section>

      <section class="matrix-section">
        <div class="section-header v-reveal">
          <h2 class="section-title">03 / 服务矩阵</h2>
        </div>
        <div class="matrix-grid">
          <div v-for="(item, index) in serviceMatrix" :key="index" class="matrix-item v-reveal">
            <div class="m-index">0{{ index + 1 }}</div>
            <div class="m-content">
              <h4>{{ item.name }}</h4>
              <p>{{ item.detail }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="advantage-section v-reveal">
        <div class="adv-card">
          <div class="adv-label">WHY LAIQIU</div>
          <h2 class="adv-title">我们不一样的地方</h2>
          <div class="adv-grid">
            <div v-for="adv in advantages" :key="adv.title" class="adv-item">
              <div class="adv-num">{{ adv.title }}</div>
              <p>{{ adv.content }}</p>
            </div>
          </div>
        </div>
      </section>

      <footer class="cap-footer v-reveal">
        <div class="footer-line"></div>
        <h3>以能力支撑热爱，以服务陪伴成长</h3>
        <p>来秋社区，持续为每一位用户提供更稳定、更贴心、更完整的体验。</p>
        <button class="cta-btn">进入社区体验</button>
        <div class="copyright">LAIQIU NETWORK © 2026. ALL RIGHTS RESERVED.</div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const mousePos = ref({ x: 0, y: 0 });
const parallaxStyle = ref({});

const handleParallax = (e) => {
  const { clientX, clientY } = e;
  const x = (clientX / window.innerWidth - 0.5) * 40;
  const y = (clientY / window.innerHeight - 0.5) * 40;
  parallaxStyle.value = {
    transform: `translate(${x}px, ${y}px)`,
  };
};

const corePillars = [
  {
    title: '内容创作能力',
    desc: '支持富文本、代码高亮、文章管理，轻松发布技术笔记、生活感悟，让创作更简单。',
    tags: ['富文本', '代码高亮', '知识沉淀'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>'
  },
  {
    title: 'AI智能服务',
    desc: '自研多模态AI能力，提供文本、图像、逻辑推理支持，为创作与学习提供智能辅助。',
    tags: ['多模态', '智能创作', '学习助手'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/><path d="M9 10a1 1 0 110-2 1 1 0 010 2zm6 0a1 1 0 110-2 1 1 0 010 2zm-7 5s1 2 3 2 3-2 3-2"/></svg>'
  },
  {
    title: '社交互动体系',
    desc: '完整好友、私信、动态系统，快速连接同好，轻松交流技术、分享生活、建立圈子。',
    tags: ['即时通讯', '好友关注', '兴趣连接'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>'
  },
  {
    title: '数字生活工具',
    desc: '集合MBTI测试、音乐、摄影、视觉小说等娱乐功能，丰富社区日常体验。',
    tags: ['趣味测试', '影音娱乐', '作品展示'],
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>'
  }
];

const serviceMatrix = [
  { name: '内容发布系统', detail: '简洁易用的编辑器，支持代码、图文、排版，一键发布分享。' },
  { name: 'AI创作助手', detail: '智能辅助写作、生成内容，提升学习与创作效率。' },
  { name: '来秋商店', detail: '虚拟装扮、积分兑换、会员服务，打造个性化社区形象。' },
  { name: '社区互动中心', detail: '话题讨论、评论点赞、关注互动，轻松找到同好。' },
  { name: '个人管理中心', detail: '文章管理、积分查看、消息通知，一站式掌握个人动态。' },
  { name: '全站搜索能力', detail: '快速查找文章、用户、话题，精准定位所需内容。' }
];

const advantages = [
  { title: '体验友好', content: '界面简洁流畅，操作直观易懂，新手也能快速上手。' },
  { title: '生态完整', content: '技术+生活双驱动，创作、社交、娱乐一站式满足。' },
  { title: '氛围温暖', content: '友善互助、尊重包容，拒绝戾气，做有温度的社区。' }
];

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active');
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.v-reveal').forEach(el => observer.observe(el))
});
</script>

<style scoped lang="scss">
$black: #000000;
$gold: #c9a063;
$gray: #111111;
$text-dim: #666;
$text-light: #ededed;
$border: rgba(255, 255, 255, 0.08);

/* -------------- 根容器：彻底清除滚动冲突 -------------- */
.cap-page {
  width: 100%;
  min-height: 100vh;
  background-color: $black;
  color: #fff;
  font-family: 'Inter', 'PingFang SC', sans-serif;
  position: relative;
  overflow: visible;
}

/* 背景装饰 */
.bg-noise {
  position: fixed;
  inset: 0;
  background: url('https://grainy-gradients.vercel.app/noise.svg');
  opacity: 0.03;
  pointer-events: none;
  z-index: 1;
}

.ambient-glow {
  position: fixed;
  width: 60vw;
  height: 60vw;
  background: radial-gradient(circle, rgba($gold, 0.08) 0%, transparent 70%);
  top: -10vw;
  right: -10vw;
  z-index: 0;
  pointer-events: none;
}

/* 内容包裹层：安全布局 */
.container-wrapper {
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
}

/* 动画 */
.v-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 1.2s cubic-bezier(0.16, 1, 0.3, 1);

  &.active {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hero */
.hero-section {
  width: 100%;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 120px 0 80px;
  position: relative;

  .tagline {
    font-size: 12px;
    letter-spacing: 5px;
    color: $gold;
    margin-bottom: 24px;
  }

  .main-title {
    font-size: clamp(3rem, 10vw, 6rem);
    font-weight: 700;
    margin-bottom: 30px;
    letter-spacing: -2px;

    .dot {
      color: $gold;
    }
  }

  .hero-desc {
    font-size: 18px;
    color: $text-dim;
    max-width: 650px;
    line-height: 1.8;
    font-weight: 300;
  }

  .scroll-indicator {
    position: absolute;
    bottom: 20px;

    .mouse {
      width: 20px;
      height: 35px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 20px;
      position: relative;

      &::before {
        content: '';
        width: 2px;
        height: 6px;
        background: $gold;
        position: absolute;
        top: 8px;
        left: 50%;
        transform: translateX(-50%);
        animation: scroll-anim 2s infinite;
      }
    }
  }
}

/* 核心支柱 */
.pillars-section {
  padding: 80px 0;
}

.section-header {
  margin-bottom: 60px;
}

.section-title {
  font-size: 14px;
  color: $gold;
  letter-spacing: 3px;
  font-family: monospace;
}

.section-subtitle {
  font-size: 32px;
  font-weight: 60;
  margin-top: 10px;
}

.pillar-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1px;
  background: $border;
  border: 1px solid $border;
}

.pillar-card {
  background: $black;
  padding: 80px 60px;
  transition: 0.5s;

  &:hover {
    background: $gray;

    .pillar-icon {
      color: $gold;
      transform: scale(1.1);
    }
  }

  .pillar-icon {
    width: 48px;
    height: 48px;
    color: $text-dim;
    margin-bottom: 40px;
    transition: 0.5s;
  }

  h3 {
    font-size: 24px;
    margin-bottom: 20px;
    font-weight: 60;
  }

  p {
    color: $text-dim;
    line-height: 1.7;
    font-size: 15px;
    margin-bottom: 30px;
  }

  .pillar-tags {
    display: flex;
    gap: 12px;
    list-style: none;
    padding: 0;

    li {
      font-size: 10px;
      padding: 4px 10px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: $text-dim;
    }
  }
}

/* 技术驱动 */
.tech-infra {
  padding: 120px 0;
}

.infra-content {
  display: flex;
  align-items: center;
  gap: 100px;
}

.text-side {
  flex: 1;
}

.infra-desc {
  font-size: 20px;
  line-height: 1.8;
  color: $text-dim;
  margin: 30px 0 50px;
}

.infra-metrics {
  display: flex;
  gap: 60px;
}

.m-val {
  font-size: 42px;
  font-weight: 60;
  color: $gold;
  display: block;
}

.m-lab {
  font-size: 12px;
  color: $text-dim;
  text-transform: uppercase;
}

.visual-side {
  flex: 1;
  display: flex;
  justify-content: center;
}

.tech-orb {
  position: relative;
  width: 300px;
  height: 300px;

  .ring {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 1px solid rgba($gold, 0.2);
    border-radius: 50%;

    &.r-1 {
      width: 100%;
      height: 100%;
      animation: spin 10s linear infinite;
    }

    &.r-2 {
      width: 70%;
      height: 70%;
      border-style: dashed;
      animation: spin 15s linear infinite reverse;
    }

    &.r-3 {
      width: 40%;
      height: 40%;
      border-color: rgba($gold, 0.5);
    }
  }
}

/* 服务矩阵 */
.matrix-section {
  padding: 80px 0;
}

.matrix-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px 40px;
}

.matrix-item {
  .m-index {
    font-family: monospace;
    color: $gold;
    font-size: 12px;
    margin-bottom: 15px;
  }

  h4 {
    font-size: 18px;
    margin-bottom: 12px;
    font-weight: 60;
  }

  p {
    font-size: 14px;
    color: $text-dim;
    line-height: 1.6;
  }
}

/* 优势 */
.advantage-section {
  padding: 120px 0;
}

.adv-card {
  background: $gray;
  border: 1px solid $border;
  padding: 100px 80px;
  text-align: center;

  .adv-label {
    font-size: 12px;
    letter-spacing: 4px;
    color: $text-dim;
    margin-bottom: 20px;
  }

  .adv-title {
    font-size: 42px;
    margin-bottom: 80px;
  }
}

.adv-grid {
  display: flex;
  justify-content: space-between;
  gap: 40px;
  text-align: left;

  .adv-num {
    font-size: 14px;
    color: $gold;
    margin-bottom: 20px;
    font-weight: 60;
  }

  p {
    color: $text-dim;
    line-height: 1.6;
  }
}

/* 底部 */
.cap-footer {
  padding: 120px 0 80px;
  text-align: center;

  .footer-line {
    width: 1px;
    height: 100px;
    background: linear-gradient($gold, transparent);
    margin: 0 auto 50px;
  }

  h3 {
    font-size: 32px;
    margin-bottom: 20px;
  }

  p {
    color: $text-dim;
    margin-bottom: 40px;
  }

  .cta-btn {
    background: transparent;
    border: 1px solid $gold;
    color: $gold;
    padding: 16px 40px;
    font-size: 14px;
    font-weight: 60;
    letter-spacing: 2px;
    cursor: pointer;
    transition: 0.3s;

    &:hover {
      background: $gold;
      color: $black;
      box-shadow: 0 0 30px rgba($gold, 0.3);
    }
  }

  .copyright {
    margin-top: 100px;
    font-size: 11px;
    color: #333;
  }
}

@keyframes scroll-anim {
  0% {
    transform: translate(-50%, 0);
    opacity: 0;
  }

  50% {
    opacity: 1;
  }

  100% {
    transform: translate(-50%, 15px);
    opacity: 0;
  }
}

@keyframes spin {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

/* 响应式 */
@media (max-width: 992px) {
  .pillar-grid {
    grid-template-columns: 1fr;
  }

  .infra-content {
    flex-direction: column;
    text-align: center;
  }

  .matrix-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .adv-grid {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .matrix-grid {
    grid-template-columns: 1fr;
  }

  .container-wrapper {
    padding: 0 24px;
  }
}
</style>